import React, { useMemo, useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Copy, Download, Printer, QrCode } from 'lucide-react';

/**
 * The practice's patient-intake QR code and link.
 *
 * Shared by Admin → Settings and the last step of signup (§2 admin note,
 * full-flow review 2026-09-06): signup used to end at "Invite your team" and
 * never showed the one thing a practice needs on day one — the link patients
 * scan. `title`/`intro` let signup speak in its own voice; everything else is
 * the same card.
 */
export function PatientIntakeQR({ practice, packetTypes, title = 'Patient Intake — QR Code & Link', intro = null, className = 'mt-4' }) {
  const [packetTypeId, setPacketTypeId] = useState('fmla_std');
  const qrCanvasRef = useRef(null);

  const intakeUrl = useMemo(() => {
    if (!practice?.id) return '';
    const base = `${window.location.origin}/intake/${practice.id}`;
    return packetTypeId ? `${base}?packet_type_id=${packetTypeId}` : base;
  }, [practice?.id, packetTypeId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(intakeUrl);
      toast.success('Intake link copied');
    } catch {
      toast.message(intakeUrl);
    }
  };

  const downloadPng = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('QR code is still rendering — try again in a second');
      return;
    }
    const filename = `${practice?.name?.replace(/\s+/g, '_') || 'practice'}_intake_qr.png`;
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Could not generate the PNG'); return; }
      // Show in the in-app viewer (works even where downloads are blocked);
      // the viewer has a Download button, and right-click > Save also works.
      const url = URL.createObjectURL(blob);
      window.dispatchEvent(new CustomEvent('fmla:view-file', { detail: { url, filename, type: 'image/png' } }));
    }, 'image/png');
  };

  const printSheet = () => {
    const w = window.open('', '_blank', 'width=600,height=800');
    if (!w) return;
    const svg = qrCanvasRef.current?.querySelector('svg')?.outerHTML || '';
    w.document.write(`
      <html><head><title>${practice?.name || 'Practice'} - Patient Intake</title>
      <style>
        body { font-family: 'IBM Plex Sans', system-ui, sans-serif; padding: 40px; text-align: center; color: #0f172a; }
        h1 { font-size: 22px; margin: 0 0 4px; }
        h2 { font-size: 14px; font-weight: normal; color: #475569; margin: 0 0 32px; }
        .qr { margin: 24px auto; }
        .url { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 12px; color: #475569; word-break: break-all; margin-top: 16px; }
        .footer { margin-top: 40px; font-size: 11px; color: #64748b; max-width: 380px; margin-left: auto; margin-right: auto; }
      </style>
      </head><body>
        <h1>${practice?.name || 'Practice'}</h1>
        <h2>Scan to start your medical leave documentation</h2>
        <div class="qr">${svg}</div>
        <div class="url">${intakeUrl}</div>
        <p class="footer">Your form is private and secure. After submitting, your medical provider will review and sign the document for you.</p>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => { w.print(); }, 200);
  };

  return (
    <Card className={className} data-testid="admin-patient-intake-qr-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><QrCode className="h-4 w-4 text-primary" /> {title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {intro || (
            <>
              Print this QR for your front desk, waiting room, or include in a welcome email.
              Patients who scan it get a fresh secure form for <strong>{practice?.name}</strong> — no account required.
              Each scan creates a new private submission that links straight to your staff queue.
            </>
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="rounded-xl border bg-white p-6 grid place-items-center" ref={qrCanvasRef}>
            {intakeUrl && (
              <>
                <QRCodeSVG
                  value={intakeUrl}
                  size={220}
                  level="M"
                  includeMargin
                  imageSettings={{
                    src:
                      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="hsl(198,84%,28%)"/><text x="12" y="17" font-family="Inter,sans-serif" font-weight="700" font-size="13" fill="white" text-anchor="middle">F</text></svg>',
                    height: 32,
                    width: 32,
                    excavate: true,
                  }}
                />
                {/* Hidden canvas used only for PNG download */}
                <div style={{ display: 'none' }}>
                  <QRCodeCanvas value={intakeUrl} size={512} level="M" includeMargin />
                </div>
              </>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label>Default packet type for this QR</Label>
              <Select value={packetTypeId} onValueChange={setPacketTypeId}>
                <SelectTrigger data-testid="admin-intake-packet-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {packetTypes.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">Patients can ask the medical staff to change this later if needed.</div>
            </div>
            <div>
              <Label>Public intake URL</Label>
              <div className="flex gap-2">
                <Input value={intakeUrl} readOnly className="font-mono text-xs" data-testid="admin-intake-url" />
                <Button type="button" variant="outline" size="icon" onClick={copyLink} data-testid="admin-intake-copy"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" onClick={downloadPng} data-testid="admin-intake-download-png"><Download className="h-4 w-4 mr-1" /> Download PNG</Button>
              <Button variant="outline" onClick={printSheet} data-testid="admin-intake-print"><Printer className="h-4 w-4 mr-1" /> Print sheet</Button>
              <Button variant="outline" onClick={() => window.open(intakeUrl, '_blank', 'noopener')} data-testid="admin-intake-test"><QrCode className="h-4 w-4 mr-1" /> Test in new tab</Button>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              Tip: change the packet-type above to print separate QR codes for STD-only or ADA workflows.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
